import { ChevronRight } from 'lucide-react';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@column/ui';

import { CopyRow, CopyRowValue } from '~/components/CopyRow';
import { type OverrideTarget } from '~/lib/approvals/hooks/useApprovalTargets';
import { collapseTransferChanges } from '~/lib/approvals/roleDiff';
import { type FieldChange, type OverrideChange, type PermissionDiff } from '~/lib/approvals/types';
import {
  AccessIconDefault,
  AccessIconEdit,
  AccessIconFullAccess,
  AccessIconNoAccess,
  AccessIconViewOnly,
} from '~/lib/roles/components/AccessIcons';

const CHANGE_GRID_GAP_PX = 20;
const MAX_OVERRIDE_CARDS = 5;
const PERMISSIONS_BREAKPOINT = '548px';

const accessIcons: Record<string, React.ReactNode> = {
  'No Access': <AccessIconNoAccess />,
  'View Only': <AccessIconViewOnly />,
  'Edit Access': <AccessIconEdit />,
  'Full Access': <AccessIconFullAccess />,
  Default: <AccessIconDefault />,
};

const cardClassName = 'rounded-lg border border-gray-700/10 bg-gray-25 dark:border-white/10 dark:bg-gray-900';

const AccessLevel: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex items-center gap-[3px] [&_svg]:[--icon-size-x:12px] [&_svg]:[--icon-size-y:12px]">
    {accessIcons[label]}
    {label}
  </span>
);

const ChangeGrid: React.FC<{ changes: FieldChange[] }> = ({ changes }) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [isSingleColumn, setIsSingleColumn] = useState(false);
  const items = useMemo(() => collapseTransferChanges(changes), [changes]);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    const measure = () => {
      const columnWidth = (grid.clientWidth - CHANGE_GRID_GAP_PX) / 2;
      const widest = Math.max(
        0,
        ...Array.from(grid.querySelectorAll<HTMLElement>('[data-change-text]')).map((el) => el.offsetWidth)
      );
      setIsSingleColumn(widest > columnWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [items]);

  return (
    <div
      ref={gridRef}
      className={cn(
        'grid gap-x-5 gap-y-3 text-xs text-muted-foreground',
        isSingleColumn ? 'grid-cols-1' : 'grid-cols-2'
      )}
    >
      {items.map((change) => (
        <div key={change.label} className="min-w-0 whitespace-nowrap">
          <div className="truncate font-medium text-foreground">
            <span data-change-text>{change.label}</span>
          </div>
          <div className="truncate">
            <span data-change-text className="inline-flex items-center gap-1">
              <AccessLevel label={change.from} />
              <span aria-hidden className="select-none">
                →
              </span>
              <AccessLevel label={change.to} />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const PermissionSection: React.FC<{ title: React.ReactNode; onClick?: () => void; children?: React.ReactNode }> = ({
  title,
  onClick,
  children,
}) => (
  <div className={cn('overflow-hidden', cardClassName)}>
    <div className="flex w-full min-w-0 items-center gap-1.5 px-3 py-2.5 text-left text-xs font-medium text-foreground">
      {onClick ? (
        <button
          type="button"
          className="group/link -my-1 -mr-1 -ml-1.5 inline-flex max-w-full min-w-0 cursor-pointer items-center rounded-md py-1 pr-1 pl-1.5 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          onClick={onClick}
        >
          {title}
        </button>
      ) : (
        title
      )}
    </div>
    {children && (
      <>
        <div className="border-t border-gray-700/10 dark:border-white/10" />
        <div className="p-3">{children}</div>
      </>
    )}
  </div>
);

const OverrideTitle: React.FC<{ name: string; isLink: boolean }> = ({ name, isLink }) => (
  <span className="inline-flex min-w-0 items-center gap-0.5 font-medium text-foreground">
    <span className="truncate">{name}</span>
    {isLink && (
      <ChevronRight className="size-3 shrink-0 opacity-0 transition-opacity duration-200 group-hover/link:opacity-100" />
    )}
  </span>
);

const SectionList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <CopyRowValue $breakpoint={PERMISSIONS_BREAKPOINT} className="min-w-0">
    <div className="mr-2 grid gap-4 py-2">{children}</div>
  </CopyRowValue>
);

const OverrideCards: React.FC<{
  overrides: OverrideChange[];
  targets: Map<string, OverrideTarget>;
  onNavigate: (path: string) => void;
}> = ({ overrides, targets, onNavigate }) => (
  <SectionList>
    {overrides.slice(0, MAX_OVERRIDE_CARDS).map((override) => {
      const target = targets.get(override.id);
      const path = target?.path;
      return (
        <PermissionSection
          key={override.id}
          title={<OverrideTitle name={target?.name ?? override.id} isLink={!!path} />}
          onClick={path ? () => onNavigate(path) : undefined}
        >
          {override.changes.length > 0 && <ChangeGrid changes={override.changes} />}
        </PermissionSection>
      );
    })}
    {overrides.length > MAX_OVERRIDE_CARDS && (
      <div className={cn('px-3 py-1.5 text-center text-xs text-muted-foreground', cardClassName)}>
        And {overrides.length - MAX_OVERRIDE_CARDS} more...
      </div>
    )}
  </SectionList>
);

export const PermissionChanges: React.FC<{
  diff: PermissionDiff;
  targets: Map<string, OverrideTarget>;
  onNavigate: (path: string) => void;
}> = ({ diff, targets, onNavigate }) => (
  <>
    {diff.details.length + diff.platform.length > 0 && (
      <CopyRow
        label="Permissions"
        breakpoint={PERMISSIONS_BREAKPOINT}
        value={
          <SectionList>
            {diff.details.length > 0 && (
              <PermissionSection title="Details">
                <ChangeGrid changes={diff.details} />
              </PermissionSection>
            )}
            {diff.platform.length > 0 && (
              <PermissionSection title="Platform">
                <ChangeGrid changes={diff.platform} />
              </PermissionSection>
            )}
          </SectionList>
        }
      />
    )}
    {diff.entities.length > 0 && (
      <CopyRow
        isSubRow
        breakpoint={PERMISSIONS_BREAKPOINT}
        label="Entity Permissions"
        value={<OverrideCards overrides={diff.entities} targets={targets} onNavigate={onNavigate} />}
      />
    )}
    {diff.bankAccounts.length > 0 && (
      <CopyRow
        isSubRow
        breakpoint={PERMISSIONS_BREAKPOINT}
        label="Bank Account Permissions"
        value={<OverrideCards overrides={diff.bankAccounts} targets={targets} onNavigate={onNavigate} />}
      />
    )}
  </>
);
